import {
  type AWSAutoScalingGroupExternalActionRequest,
  type AWSSecurityGroupsExternalActionRequest,
  type ConcurrentNode,
  type ContinuousStatusCheckNode,
  type DelayNode,
  type ExternalActionNode,
  type ExternalActionRequest,
  type FailureFlagNode,
  type GraphNode,
  type InfraAttackNode,
  type RollbackExternalActionNode,
  ScenarioGraphNodeType,
  type SynchronousStatusCheckNode
} from 'types/api/scenarios/scenario'

/*
 * this is intentionally vague to allow the type guard functions below to accept various node values
 * and validate that they are the correct type of node  without throwing TS errors at their call point
 */
export type PossibleGraphNode = Record<string, any> | null

export function isGraphBranch(node: PossibleGraphNode) {
  return node?.hasOwnProperty('start_id')
}

export function isGraphNode(node: PossibleGraphNode): node is GraphNode {
  return (
    node != null &&
    node.hasOwnProperty('type') &&
    Object.values(ScenarioGraphNodeType).includes(node.type)
  )
}

export const isGraphNodeType = (
  node: PossibleGraphNode,
  type: ScenarioGraphNodeType
) => node?.type === type

export function isInfraAttackNode(
  node: PossibleGraphNode | undefined
): node is InfraAttackNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.InfraAttack)
  )
}

export function isFailureFlagNode(
  node: PossibleGraphNode | undefined
): node is FailureFlagNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.FailureFlag)
  )
}

export function isExternalActionNode(
  node: PossibleGraphNode | undefined
): node is ExternalActionNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.ExternalAction)
  )
}

export function isRollbackExternalActionNode(
  node: PossibleGraphNode | undefined
): node is RollbackExternalActionNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.RollbackExternalAction)
  )
}

export function isAWSExternalActionRequest(
  externalActionRequest: ExternalActionRequest
): externalActionRequest is
  | AWSAutoScalingGroupExternalActionRequest
  | AWSSecurityGroupsExternalActionRequest {
  return (
    externalActionRequest !== undefined &&
    'resourceArn' in externalActionRequest
  )
}

export function isDelayNode(node: PossibleGraphNode): node is DelayNode {
  return isGraphNodeType(node, ScenarioGraphNodeType.Delay)
}

export function isContinuousStatusCheckNode(
  node: PossibleGraphNode
): node is ContinuousStatusCheckNode {
  return isGraphNodeType(node, ScenarioGraphNodeType.ContinuousStatusCheck)
}

export function isSynchronousStatusCheckNode(
  node: PossibleGraphNode | undefined
): node is SynchronousStatusCheckNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.SynchronousStatusCheck)
  )
}

export function isConcurrentNode(
  node: PossibleGraphNode
): node is ConcurrentNode {
  return isGraphNodeType(node, ScenarioGraphNodeType.Concurrent)
}

export function isChildGraphNode(node: PossibleGraphNode): boolean {
  return (
    isInfraAttackNode(node) ||
    isSynchronousStatusCheckNode(node) ||
    isDelayNode(node) ||
    isConcurrentNode(node)
  )
}
